import React from "react";
import { Eye, Share2, Download, X, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import {
  canShareFile,
  shareFile,
  downloadBlob,
  previewBlob,
  formatFileSize,
} from "../lib/file-delivery";

// Bottom sheet shown on mobile after a report file is generated.
// Gives the user: in-browser preview (PDF), native share (save to Files /
// WhatsApp / open in a PDF viewer), or classic download.
export const FileActionSheet = ({ file, onClose }) => {
  if (!file) return null;
  const { blob, filename, mime } = file;
  const isPdf = mime === "application/pdf";
  const shareable = canShareFile(blob, filename, mime);
  const Icon = isPdf ? FileText : FileSpreadsheet;

  const handleShare = async () => {
    try {
      const shared = await shareFile(blob, filename, mime);
      if (shared) onClose();
    } catch {
      toast.error("تعذرت المشاركة، جرّب التنزيل");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="file-action-sheet">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 pb-8 sm:pb-5 fade-up shadow-2xl" dir="rtl">
        <div className="sm:hidden w-10 h-1 rounded-full bg-[#1A1C19]/15 mx-auto mb-4" />
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-md bg-[#1F3A33] text-white flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="font-readex font-semibold text-[#1A1C19]">الملف جاهز ✓</div>
              <div className="text-xs text-[#8A8D86] truncate" dir="ltr">
                {filename} {blob?.size ? `· ${formatFileSize(blob.size)}` : ""}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 -me-2 text-[#5C605A]" aria-label="إغلاق" data-testid="file-sheet-close-btn">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="space-y-2.5">
          {isPdf && (
            <button
              onClick={() => previewBlob(blob)}
              data-testid="file-sheet-preview-btn"
              className="w-full flex items-center gap-3 bg-[#1F3A33] text-white rounded-md px-4 py-3.5 font-readex font-medium active:scale-[0.99] transition-transform"
            >
              <Eye className="w-5 h-5" strokeWidth={1.5} />
              <span>معاينة الملف الآن</span>
            </button>
          )}
          {shareable && (
            <button
              onClick={handleShare}
              data-testid="file-sheet-share-btn"
              className="w-full flex items-center gap-3 bg-[#6A8A76] text-white rounded-md px-4 py-3.5 font-readex font-medium active:scale-[0.99] transition-transform"
            >
              <Share2 className="w-5 h-5" strokeWidth={1.5} />
              <span>مشاركة / حفظ في الملفات</span>
            </button>
          )}
          <button
            onClick={() => { downloadBlob(blob, filename); onClose(); }}
            data-testid="file-sheet-download-btn"
            className="w-full flex items-center gap-3 bg-white border border-[#1F3A33]/30 text-[#1F3A33] rounded-md px-4 py-3.5 font-readex font-medium active:scale-[0.99] transition-transform"
          >
            <Download className="w-5 h-5" strokeWidth={1.5} />
            <span>تنزيل إلى الجهاز</span>
          </button>
        </div>
      </div>
    </div>
  );
};
